import React from 'react';
import PageWithAsyncData from '../components/PageWithAsyncData';
import { useTeamsProfile } from '../hooks/use-hydra';
import styled, { useTheme, css } from 'styled-components';
import { useHistory, Prompt } from 'react-router-dom';
import { toast } from 'react-toastify';
import { H3, PExtraSmall } from '../components/elements/Text';
import { ColumnQuestionWrapper } from '../components/Onboarding/Typeform/common';
import autoAnimate from '@formkit/auto-animate';
import useAuth from '../hooks/use-auth';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  SkillRatingQuestion,
  InputQuestion,
  NavigationFooter,
  AnimationContainer,
  ScrollContainer,
  NavButton,
} from './Onboarding/TeamsCohortOnboarding';
import { ProgressWidget } from './Onboarding/TeamsProfileOnboarding';
import { CohortFeedbackSurvey, postCohortFeedback } from '../api/teams';
import { useMutation } from 'react-query';
import { useTeamsProjectsDashboard } from '../hooks/use-hydra/teams';
import { ROUTES } from '../routing';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

type SkillRatingQuestionType = {
  id: string;
  type: 'SKILL_RATING';
  text: string;
  topics: string[];
};

type RatingQuestionType = {
  id: string;
  type: 'RATING';
  text: string;
  required: boolean;
};

type FreeTextQuestionType = {
  id: string;
  type: 'FREE_TEXT';
  text: string;
  required: boolean;
};

type QuestionType =
  | SkillRatingQuestionType
  | RatingQuestionType
  | FreeTextQuestionType;

const retroSurveyQuestions: (RatingQuestionType | FreeTextQuestionType)[] = [
  {
    id: 'ask-recommend-enki',
    type: 'RATING',
    text: `To what extent do you agree with: I would recommend Enki to my colleagues in general.`,
    required: true,
  },
  {
    id: 'ask-learned-efficiency',
    type: 'RATING',
    text: `To what extent do you agree with: I learned things during the Enki cohort that will make me more efficient at work.`,
    required: true,
  },
  {
    id: 'ask-learned-decision-making',
    type: 'RATING',
    text: `To what extent do you agree with: I learned things during the Enki cohort that will improve my decision-making at work.`,
    required: true,
  },
  {
    id: 'ask-mentor-experience',
    type: 'RATING',
    text: 'To what extent do you agree with: Having a mentor helped me a lot during the Enki cohort.',
    required: true,
  },
  {
    id: 'ask-mentor-comments',
    type: 'FREE_TEXT',
    text: 'Would you like to share anything specific about your experience with your Enki mentor? (Note: this will not be seen by them.)',
    required: true,
  },
  {
    id: 'ask-cohort-expectations',
    type: 'FREE_TEXT',
    text: `Did the Enki cohort meet your expectations? Why / why not?`,
    required: true,
  },
  {
    id: 'ask-content-improvements',
    type: 'FREE_TEXT',
    text: 'What, if anything, could have improved the content and/or learning experience for you?',
    required: true,
  },
];

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export default function CohortFeedback() {
  const {
    value: teamsProfile,
    retry: reloadProfile,
    loading: isLoadingTeams,
    error,
  } = useTeamsProfile();
  const { value: projects } = useTeamsProjectsDashboard();
  const { getTokenSilently } = useAuth();
  const history = useHistory();
  const theme = useTheme();

  const learningPlanTopics = React.useMemo(() => {
    const moduleTopics = (teamsProfile?.populatedLearningPlan ?? [])
      .filter((topic) => topic.type === 'content-module')
      .map((topic) => {
        const module = teamsProfile?.modules.find(
          (m) => m.content.slug === topic.slug
        );
        return module?.content?.topic;
      })
      .filter((topic): topic is string => topic != null);
    const projectTopics = (teamsProfile?.populatedLearningPlan ?? [])
      .filter((topic) => topic.type === 'project')
      .map((topic) => {
        const project = projects?.find((p) => p.slug === topic.slug);
        return project?.topic;
      })
      .filter((topic): topic is string => topic != null);
    return Array.from(new Set([...moduleTopics, ...projectTopics]));
  }, [teamsProfile, projects]);

  const QUESTIONS: QuestionType[] = React.useMemo(() => {
    const questions: QuestionType[] = [];
    if (learningPlanTopics.length > 0) {
      questions.push({
        id: 'offboarding-skill-rating',
        type: 'SKILL_RATING',
        text:
          learningPlanTopics.length === 1
            ? `On a scale of 0-10, how would you rate yourself now (after this cohort!) in ${learningPlanTopics[0]}?`
            : 'On a scale of 0-10, how would you rate yourself now (after this cohort!) in each of the skills below?',
        topics: learningPlanTopics,
      });
    }
    return [...questions, ...retroSurveyQuestions];
  }, [learningPlanTopics]);

  const latestCohort = teamsProfile?.onboardedCohorts.sort(
    (a, b) =>
      dayjs.utc(b.cohort.startDate).valueOf() -
      dayjs.utc(a.cohort.startDate).valueOf()
  )[0];

  const [answers, setAnswers] = React.useState<
    Record<string, CohortFeedbackSurvey[string]['answer']>
  >({});

  React.useEffect(() => {
    const currentFeedback = latestCohort?.milestones?.COHORT_FEEDBACK?.survey;
    if (currentFeedback) {
      const result: Record<string, CohortFeedbackSurvey[string]['answer']> = {};
      for (const [id, value] of Object.entries(currentFeedback)) {
        result[id] = value.answer;
      }
      setAnswers(result);
    }
  }, [latestCohort]);

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const animationParentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (animationParentRef.current) {
      autoAnimate(animationParentRef.current, {
        duration: 300,
        easing: 'ease-in-out',
      });
    }
  }, [animationParentRef]);

  const currentQuestion = QUESTIONS[currentIndex];
  const currentAnswer = answers[currentQuestion?.id];

  // Validation logic
  const isCurrentQuestionAnswered = React.useMemo(() => {
    if (!currentQuestion) return false;
    if (currentQuestion.type === 'SKILL_RATING') {
      if (!currentAnswer || typeof currentAnswer !== 'object') return false;
      return (currentQuestion as SkillRatingQuestionType).topics.every(
        (topic: string) =>
          typeof currentAnswer[topic] === 'number' &&
          currentAnswer[topic] >= 0 &&
          currentAnswer[topic] <= 10
      );
    }
    if (currentQuestion.type === 'RATING') {
      return (
        typeof currentAnswer === 'number' &&
        currentAnswer >= 1 &&
        currentAnswer <= 5
      );
    }
    if (currentQuestion.type === 'FREE_TEXT') {
      return (
        typeof currentAnswer === 'string' && currentAnswer.trim().length > 0
      );
    }
    return false;
  }, [currentQuestion, currentAnswer]);

  const handleAnswerQuestion = (
    id: string,
    answer: CohortFeedbackSurvey[string]['answer']
  ) => {
    setAnswers((prev) => ({ ...prev, [id]: answer }));
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const submitFeedback = useMutation({
    mutationFn: async () => {
      const cohortId = teamsProfile?.onboardedCohorts?.[0]?.cohort?.id;
      const learnerId = teamsProfile?.learnerId;
      if (!cohortId || !learnerId) {
        toast.error('Could not determine cohort or learner.');
        return;
      }
      const token = await getTokenSilently();
      const parsedAnswers: Record<
        string,
        { answer: CohortFeedbackSurvey[string]['answer']; question: string }
      > = {};
      Object.entries(answers).forEach(([id, answer]) => {
        parsedAnswers[id] = {
          answer,
          question: QUESTIONS.find((q) => q.id === id)?.text ?? '',
        };
      });
      await postCohortFeedback(token, {
        cohortId: String(cohortId),
        learnerId: String(learnerId),
        survey: parsedAnswers,
      });
    },
    onSuccess: async () => {
      await reloadProfile({});
      toast.success('Feedback submitted!');
      history.push(ROUTES.HOME.getPath());
    },
    onError: () => {
      toast.error('Failed to submit feedback. Please try again.');
    },
  });
  const hasUnsavedChanges = React.useMemo(() => {
    if (submitFeedback.isLoading) return false;
    const currentAnswers = Object.keys(answers);
    const initialFeedback = latestCohort?.milestones?.COHORT_FEEDBACK?.survey;
    const initialAnswers = initialFeedback
      ? Object.values(initialFeedback).map((value) => value.answer)
      : [];
    return (
      currentAnswers.length !== initialAnswers.length ||
      currentAnswers.some(
        (key) => answers[key] !== (initialFeedback?.[key]?.answer ?? null)
      )
    );
  }, [answers, latestCohort, submitFeedback.isLoading]);

  const handleNext = async () => {
    if (!isCurrentQuestionAnswered) return;
    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      submitFeedback.mutate();
    }
  };

  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return (
    <PageWithAsyncData
      isLoading={isLoadingTeams}
      error={error}
      retry={reloadProfile}
    >
      <Prompt
        when={hasUnsavedChanges}
        message="You have unsaved changes. Are you sure you want to leave this page?"
      />
      <ScrollContainer ref={animationParentRef}>
        <AnimationContainer key={`animation-${currentQuestion?.id}`}>
          <Column key={`column-${currentQuestion?.id}`}>
            {currentQuestion?.type === 'SKILL_RATING' && (
              <SkillRatingQuestion
                id={currentQuestion.id}
                text={currentQuestion.text}
                topics={(currentQuestion as SkillRatingQuestionType).topics}
                onAnswerQuestion={handleAnswerQuestion}
                answer={
                  (currentAnswer as unknown as Record<string, number>) || {}
                }
                onNext={handleNext}
              />
            )}
            {currentQuestion?.type === 'RATING' && (
              <RatingQuestion
                id={currentQuestion.id}
                text={currentQuestion.text}
                answer={(currentAnswer as unknown as number) || 0}
                onAnswerQuestion={handleAnswerQuestion}
              />
            )}
            {currentQuestion?.type === 'FREE_TEXT' && (
              <InputQuestion
                id={currentQuestion.id}
                text={currentQuestion.text}
                answer={currentAnswer as string}
                onAnswerQuestion={handleAnswerQuestion}
                onNext={handleNext}
              />
            )}
            <NavButton
              label={currentIndex === QUESTIONS.length - 1 ? 'Submit' : 'Next'}
              onClick={handleNext}
              isLoading={submitFeedback.isLoading}
              isDisabled={!isCurrentQuestionAnswered}
              style={{ marginTop: '32px' }}
              type="primary"
              faIcon={
                currentIndex === QUESTIONS.length - 1
                  ? 'fas fa-check'
                  : 'fas fa-arrow-right'
              }
            />
          </Column>
        </AnimationContainer>
        <NavigationFooter>
          <NavButton
            label="Previous"
            onClick={handlePrevious}
            faIcon="fas fa-arrow-left"
            isDisabled={currentIndex === 0}
            showIconBeforeLabel
            color={theme.colors.textRegular}
            style={{ border: 'none', marginTop: 0 }}
          />
          <ProgressWidget
            label="COHORT FEEDBACK"
            currentIndex={currentIndex}
            totalQuestions={QUESTIONS.length}
          />
        </NavigationFooter>
      </ScrollContainer>
    </PageWithAsyncData>
  );
}

// RatingQuestion component for 1-5 scale
function RatingQuestion({
  id,
  text,
  answer,
  onAnswerQuestion,
}: {
  id: string;
  text: string;
  answer: number;
  onAnswerQuestion: (id: string, answer: number) => void;
}) {
  return (
    <ColumnQuestionWrapper>
      <H3>{text}</H3>
      <RatingContainer>
        <RatingButtonsContainer>
          {[1, 2, 3, 4, 5].map((i) => (
            <RatingButton
              key={`rating-${id}-${i}`}
              isSelected={answer === i}
              onClick={() => onAnswerQuestion(id, i)}
              tabIndex={0}
              role="button"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onAnswerQuestion(id, i);
                }
              }}
            >
              {i}
            </RatingButton>
          ))}
        </RatingButtonsContainer>
        <RatingLabelsContainer>
          <RatingLabel style={{ width: '40px', textAlign: 'center' }}>
            Strongly Disagree
          </RatingLabel>
          <div style={{ width: '40px' }} />
          <RatingLabel style={{ width: '40px', textAlign: 'center' }}>
            Neutral
          </RatingLabel>
          <div style={{ width: '40px' }} />
          <RatingLabel style={{ width: '40px', textAlign: 'center' }}>
            Strongly Agree
          </RatingLabel>
        </RatingLabelsContainer>
      </RatingContainer>
    </ColumnQuestionWrapper>
  );
}

const RatingContainer = styled.div`
  display: flex;
  flex-direction: column;
  /* align-items: flex-start; */
  /* width: 100%; */
  margin-right: 300px;
`;

// Styled components for rating buttons and labels (copied and adapted from SkillRatingQuestion)
const RatingButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  margin-top: 32px;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const RatingButton = styled.div<{ isSelected: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: 500;
  font-size: 16px;
  border: 2px solid ${({ theme }) => theme.colors.bgActive};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all 0.3s ease-in-out;
  padding-bottom: 0;

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      border-color: ${theme.colors.buttonPrimary};
      background-color: ${theme.colors.buttonPrimary};
      color: ${theme.colors.buttonTextColor};
    `}

  &:hover {
    border-color: ${({ theme }) => theme.colors.buttonPrimary};
    background-color: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.buttonPrimary : theme.colors.bgActive};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.buttonPrimary};
    outline-offset: 2px;
  }
`;

const RatingLabelsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`;

const RatingLabel = styled(PExtraSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 12px;
  font-weight: 400;
  margin-top: 8px;
`;
